// @ts-strict

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ISingleCampaign, IPagination, IChosenSortValue, ICreatedConfigurationConfigData, SpecialCampaignData } from '@app/core/models/core-models';
import { ProductsService } from '@services/products/products.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { dropdownValues } from '@app/core/helpers/constants';
import { IGeneratedProductConfiguration, IProductData } from '@services/products/products.types';
import { IBannerConfigData } from '@app/core/services/banners/banners.service.types';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { NotificationTypes } from '@app/core/services/global-notifications/global-notifications.model';

@Component({
  selector: 'app-products-modal',
  templateUrl: './products-modal.component.html',
  styleUrls: ['./products-modal.component.scss']
})
export class ProductsModalComponent implements OnInit {
  @Input() currentCampaign?: ISingleCampaign | SpecialCampaignData;
  @Output() productConfigurationVisible = new EventEmitter<boolean>();

  public selectedProduct?: IProductData;
  public productsList: Array<IProductData> = [];
  public isConfigurationVisible = false;
  public searchBarFocused = false;
  public isLoaded = false;
  public scrolledToBottom = false;
  public productConfigurationData: IBannerConfigData = { domainID: 0, programID: 0 };
  public isSuccessfullySubmitted = false;
  public fetchingNextPage = false;
  public readonly sortClass = 'greyBackground';
  public dropdownValues = [dropdownValues[0], dropdownValues[1], dropdownValues[16], dropdownValues[17]];

  private productsPagination?: IPagination;
  private search$ = new Subject<string>();
  private sortValues: {
    order_field: string;
    order_type: string;
  } = {
    order_field: 'id',
    order_type: 'asc'
  };
  private currentFilters: any = {};

  constructor(
    private productsService: ProductsService,
    private globalNotificationsService: GlobalNotificationsService
  ) {}

  ngOnInit(): void {
    this.getProducts(1);
    this.search$.pipe(debounceTime(300), distinctUntilChanged()).subscribe(term => {
      this.currentFilters.search = term.trim();
      this.getProducts(1);
    });
  }

  public getSortValue(value: IChosenSortValue): void {
    this.sortValues.order_field = value.orderField || '';
    this.sortValues.order_type = value.orderType || '';
    this.isLoaded = false;
    this.getProducts(1);
  }

  public showProductConfig(event: boolean): void {
    this.isConfigurationVisible = event;
    this.productConfigurationVisible.emit(true);
  }

  public changeVisibility(event: boolean): void {
    this.isConfigurationVisible = event;
    this.productConfigurationVisible.emit(false);
  }

  public searchName(searchInput: string): void {
    if (this.productsList) {
      if (searchInput.trim().length >= 2) {
        this.isLoaded = false;
        this.search$.next(searchInput.trim());
      } else if (searchInput.trim().length === 0) {
        this.isLoaded = false;
        delete this.currentFilters.search;
        this.getProducts(1);
      }
    }
  }

  public addProduct(data: ICreatedConfigurationConfigData): void {
    this.productsService
      .addProductConfiguration(data.config.id || 0, this.selectedProduct?.id || 0, data.trackingDomain.id, this.currentCampaign?.id)
      .subscribe(
        res => {
          this.showNotification('product_configuration_successfully_added', NotificationTypes[0]);
          this.productConfigurationData = {
            domainID: data.trackingDomain.id,
            programID: this.currentCampaign?.id || 0,
            generatedURL: res.data?.product_feed_url || ''
          };
          // product data to push to products array
          const productToArray: IGeneratedProductConfiguration = {
            product_id: this.selectedProduct?.id,
            created_at: new Date().toISOString(),
            program_config_id: data.config.id,
            product_img: this.selectedProduct?.product_img || '',
            program_config_name: data.config.name,
            name: this.selectedProduct?.name || '',
            tracking_url: res.data?.product_feed_url || ''
          };
          this.isSuccessfullySubmitted = true;
          this.productsService.newProduct.next(productToArray);
        },
        err => {
          this.showNotification('add_product_configuration_failed', NotificationTypes[1]);
        }
      );
  }

  public checkIfScrolled(): void {
    const parentElement = document.getElementById('allProducts');

    this.scrolledToBottom = Boolean(parentElement && parentElement.scrollTop === parentElement.scrollHeight - parentElement.clientHeight);

    if (this.scrolledToBottom && (this.productsPagination?.total_pages || 1) > 1) {
      this.fetchingNextPage = true;
      this.getProducts((this.productsPagination?.current_page || 1) + 1);
    }
  }

  private getProducts(page: number): void {
    if (page === 1) {
      this.productsList = [];
    }

    this.currentFilters.page = page;
    this.productsService.getProductsList(this.currentCampaign?.id || 0, this.currentFilters, this.sortValues).subscribe(
      res => {
        const products = res.data?.products || [];
        this.productsList.push(...products);
        this.productsPagination = res.data?.pagination;
        this.isLoaded = true;
        this.fetchingNextPage = false;
      },
      err => {
        this.showNotification('error_occured', NotificationTypes[1]);
        this.fetchingNextPage = false;
        if (err.status === 403) {
          this.isLoaded = true;
          this.productsList = [];
        } else {
          console.error(err);
        }
      }
    );
  }

  private showNotification(message: string, type: (typeof NotificationTypes)[number], translate: boolean = true): void {
    this.globalNotificationsService.show({
      message: translate && !message.includes('view_translations.') ? `view_translations.${message}` : message,
      type,
      translate,
      isPersistent: false,
      id: ''
    });
  }
}
